/* Base */

.eds-compound-input {
  --eds--color--border-input-strong: var(--eds--palette-gray-700);
  --eds--color--border-input-default: var(--eds--palette-gray-500);
  --eds--color--border-input-disabled: var(--eds--palette-gray-400);
  /* Status and focus thicken the edge with an inset ring instead of a wider
   * border, matching Input and Select: `width: 100%` cannot absorb a
   * border-width change, so a 2px border would shift the box inside a
   * fixed-width field. Here it would also jump the far segment sideways. */
  --_edge: var(--eds--color--border-input-default);
  --_edge-ring: none;
  background: var(--eds--color--bg-base);
  border: var(--eds--size-1) solid var(--_edge);
  border-radius: var(--eds--radii-8);
  box-shadow: var(--_edge-ring);
  box-sizing: border-box;
  /* The segments set their own type; this is here so the `ch` a hug segment is
   * sized in measures the field's text rather than whatever the host inherits. */
  font: var(--eds--typography--body-m-font);
  /* Segments are seamless, so their corners have to be clipped by this radius. */
  overflow: hidden;
  width: 100%;
}

/* Segments */

.eds-compound-input--segment {
  min-width: 0;
}

.eds-compound-input--segment--fill {
  /* Basis 0 rather than the control's own intrinsic width: a number input
   * reports the browser's ~20-character default and would claim room the
   * qualifier needs to stay readable. The floor keeps a value that gave that
   * room away still wide enough to type an amount into. */
  flex: 1 1 0;
  min-width: var(--eds--size-96);
}

.eds-compound-input--segment--hug {
  /* Shrinkable: a hug width is what the segment wants, not what a field too
   * narrow for both halves has to give it. */
  flex: 0 1 auto;
}

/*
 * A hug select cannot size from its content the way an input does: Select
 * paints the selected option as an absolutely positioned overlay, so the
 * trigger collapses onto its query input's 4ch floor and clips the very text
 * the segment exists to show. `--_hug-characters` carries the widest option's
 * length; the chrome around that text — inline padding, the caret, and the gap
 * before it — is added here.
 */
.eds-compound-input--segment--hug-options {
  width: max(
    var(--eds--size-96),
    calc(
      var(--_hug-characters, 0) * 1ch + var(--eds--padding-12) * 2 +
        var(--eds--spacing-8) + var(--eds--size-20)
    )
  );
}

.eds-compound-input--divider {
  align-self: stretch;
  background: var(--eds--color--border-input-default);
  /* The divider keeps the resting colour: it separates the halves, it is not
     part of the edge the status and focus rings paint. */
  flex: 0 0 auto;
  width: var(--eds--size-1);
}

.eds-compound-input--stacked .eds-compound-input--divider {
  height: var(--eds--size-1);
  width: auto;
}

/* Stacked, each segment owns the full width of the column: the sizing above
 * divides a single row, and a hug width pinned there would overflow a column
 * narrower than it. */
.eds-compound-input--stacked .eds-compound-input--segment {
  flex: 0 0 auto;
  min-width: 0;
  width: auto;
}

/* States */

.eds-compound-input:hover:not(:focus-within):not(
  .eds-compound-input--disabled
):not(.eds-compound-input--critical):not(.eds-compound-input--cautious) {
  --_edge: var(--eds--color--border-input-strong);
}

/*
 * Focus lands on the container, not the segment: both halves share one ring so
 * the pair keeps reading as a single field.
 */
.eds-compound-input:focus-within {
  --_edge: var(--eds--color--border-brand);
  --_edge-ring: inset 0 0 0 var(--eds--size-1) var(--_edge);
  outline: none;
}

.eds-compound-input--disabled {
  --_edge: var(--eds--color--border-input-disabled);
  color: var(--eds--color--fg-disabled);
  cursor: not-allowed;
  opacity: var(--eds--opacity-disabled);
}

/*
 * Status keeps the base background — unlike Input and Select, the design shows
 * a compound field tinting only its border, so a wrong half doesn't wash the
 * whole pair.
 */
.eds-compound-input--critical {
  --_edge: var(--eds--color--border-critical);
  --_edge-ring: inset 0 0 0 var(--eds--size-1) var(--_edge);
}

.eds-compound-input--critical:hover:not(:focus-within):not(
  .eds-compound-input--disabled
) {
  --_edge: var(--eds--color--fg-critical-strong);
}

.eds-compound-input--cautious {
  --_edge: var(--eds--color--border-cautious);
  --_edge-ring: inset 0 0 0 var(--eds--size-1) var(--_edge);
}

.eds-compound-input--cautious:hover:not(:focus-within):not(
  .eds-compound-input--disabled
) {
  --_edge: var(--eds--color--fg-cautious-strong);
}
